import React, {useEffect, useRef} from "react";
import {ScrollView, useWindowDimensions} from "react-native";
import {SafeAreaView} from "react-native-safe-area-context";
import {Stack, Box, TextBlock, View, Text, TextLine, Logo, Inline} from "@unibuddy/patron";
import styled, {keyframes} from "styled-components";
import {useAnalytics} from "@unibuddy/tracking";
import RelativeLink from "ubcommunity-shared/src/Navigation/RelativeLink/RelativeLink";
import RelativeButtonLink from "ubcommunity-shared/src/Navigation/RelativeButtonLink/RelativeButtonLink";
import {boldFontStyles} from "ubcommunity-shared/src/Styles/fontStyles";
import {TrackEvents, Auth} from "ubcommunity-shared/src/constants";
import {Routes} from "ubcommunity-shared/src/constants/routes";
import {SlidesItem} from "./SlidesItem";
import {slidesImages} from "./slidesImages";
import {useIsDesktop} from "ubcommunity-shared/src/General/useIsDesktop/useIsDesktop";

const slideUp = keyframes`
    0% {
        transform: translateY(1000px);
    }
    100% {
        transform: translateY(0%);
    }
`;

const fadeIn = keyframes`
    0% {
        opacity: 0
    }
    100% {
        opacity: 1
    }
`;

const SlideUpBox = styled(Box)`
    @media (max-width: 768px) {
        animation: ${slideUp} 0.75s ease;
        animation-fill-mode: forwards;
    }
`;

const FadeInBox = styled(View)`
    animation: ${fadeIn} 0.75s ease-in-out;
`;

const LogoLink = styled(RelativeLink)`
    text-decoration: none;
`;

export const communityLogo = (
    <Box display="flex" justifyContent="center" alignItems="center" padding="xxsmall">
        <LogoLink to="/auth" aria-label="Home page">
            <Inline space="small" verticalAlign="center" wrap="nowrap">
                <Logo aria-hidden displayText={false} width={22} />
                <TextBlock nowrap letterSpacing="2px" size="xxsmall">
                    <TextLine weight="700">UNI</TextLine>BUDDY
                </TextBlock>
            </Inline>
        </LogoLink>
    </Box>
);

const communityHeader = (
    <Stack space="small" align="center">
        <Text align="center" size="xlarge" color="black" style={{...boldFontStyles}}>
            Welcome to Unibuddy
        </Text>
    </Stack>
);

const CreateYourAccountButton = () => {
    const {trackEvent} = useAnalytics();

    return (
        <RelativeButtonLink
            to={Routes.SIGNUP_STEP_1}
            block
            color="primary"
            size="lg"
            onClick={() => {
                trackEvent(TrackEvents.CREATE_YOUR_ACCOUNT_CLICKED);
            }}
        >
            Create your account
        </RelativeButtonLink>
    );
};

export function Landing() {
    const scrollViewRef = useRef<ScrollView>();
    const {width, height} = useWindowDimensions();
    const {trackEvent} = useAnalytics();
    const {isDesktop} = useIsDesktop();
    useEffect(() => {
        let currentSlide = 0;
        const timer = setInterval(() => {
            const newCurrentSlide = currentSlide + 1 < slidesImages.length ? currentSlide + 1 : 0;
            const newSlidePosition = width * newCurrentSlide;
            if (scrollViewRef.current) {
                scrollViewRef.current.scrollTo({
                    animated: true,
                    x: newSlidePosition,
                });
            }
            currentSlide = newCurrentSlide;
        }, 4000);
        return () => clearInterval(timer);
    }, [width]);

    return (
        <SafeAreaView style={{flex: 1}}>
            <View flex={1} alignItems="center" maxH={["100%", "100%", 860]} overflow="hidden">
                <FadeInBox flex={2}>
                    <ScrollView
                        horizontal
                        pagingEnabled={true}
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{
                            width: width,
                        }}
                        scrollEnabled={false}
                        ref={scrollViewRef}
                        scrollEventThrottle={10}
                    >
                        {slidesImages.map((image, index) => (
                            <SlidesItem
                                image={image}
                                width={width}
                                key={`landing-page-image-${index}`}
                            />
                        ))}
                    </ScrollView>
                </FadeInBox>
                <SlideUpBox
                    minW={["100%", 375]}
                    maxW={475}
                    padding="medium"
                    flex={1}
                    paddingBottom={height < 800 ? "xxlarge" : "medium"}
                    overflow={isDesktop ? "hidden" : "auto"}
                >
                    <Stack space="medium" align="center">
                        {communityLogo}
                        {communityHeader}
                        <Stack space="large">
                            <Text align="center" size="large" color="black">
                                Join group chats, meet other students and find your group of
                                friends!
                            </Text>
                            <Stack space="medium">
                                <CreateYourAccountButton />
                                <TextBlock nowrap align="center" size="large" color="black">
                                    Have an account? {` `}
                                    <RelativeLink
                                        to={Routes.LOGIN}
                                        style={{
                                            textDecorationLine: "underline",
                                            ...boldFontStyles,
                                        }}
                                        callback={() => trackEvent(TrackEvents.LOGIN_CLICKED)}
                                    >
                                        {Auth.PRIMARY_CTA}
                                    </RelativeLink>
                                </TextBlock>
                            </Stack>
                        </Stack>
                    </Stack>
                </SlideUpBox>
            </View>
        </SafeAreaView>
    );
}
